<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">
				담은 물건
				<span v-if="institutionLabel" class="badge bg-primary" style="margin-left: 8px">{{ institutionLabel }}</span>
			</div>

			<div v-if="!list.length" style="padding: 40px 0; text-align: center; color: #888">
				담은 물건이 없습니다. 매물 상세에서 <b>물건 담기</b>를 눌러 추가하세요.
			</div>

			<div v-else>
				<!-- 전체 선택 + 안내 -->
				<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap">
					<label style="display: flex; align-items: center; gap: 4px; margin: 0">
						<input type="checkbox" v-model="allChecked" /> 전체 선택
					</label>
					<span style="color: #888">담당자별로 묶여 각각 전송됩니다. 보낼 물건만 선택하세요.</span>
				</div>

				<!-- 담당자별 그룹 -->
				<div v-for="g in groups" :key="g.key" class="cart-group">
					<div class="cart-group-head">
						<label style="display: flex; align-items: center; gap: 6px; margin: 0">
							<input type="checkbox" :checked="groupChecked(g)" @change="setGroupChecked(g, $event.target.checked)" />
							<b v-if="g.mgrUid">담당자 {{ g.mgrName }}<span v-if="g.mgrRank"> {{ g.mgrRank }}</span></b>
							<b v-else style="color: #c0392b">담당자 미지정</b>
							<span style="color: #888">({{ g.items.length }}건)</span>
						</label>
					</div>
					<div class="table-wrap">
						<table class="table">
							<colgroup>
								<col width="6%" />
								<col width="8%" />
								<col width="10%" />
								<col width="30%" />
								<col width="36%" />
								<col width="10%" />
							</colgroup>
							<tr>
								<th></th>
								<th>no</th>
								<th>구분</th>
								<th>물건명</th>
								<th>주소</th>
								<th>관리</th>
							</tr>
							<tr v-for="(it, idx) in g.items" :key="it.building_type + '_' + it.building_uid">
								<td>
									<input type="checkbox" v-model="checked[it.building_type + '_' + it.building_uid]" />
								</td>
								<td>{{ idx + 1 }}</td>
								<td>{{ it.building_type === 'rent' ? '임대' : '매매' }}</td>
								<td>{{ it.building_name }}</td>
								<td>{{ it.road_addr || it.jibun_addr }}</td>
								<td>
									<button type="button" class="btn btn-sm btn-outline-secondary" @click="btnRemove(it)">빼기</button>
								</td>
							</tr>
						</table>
					</div>
				</div>

				<div class="mt-3">
					<label class="input-label">요청 메모 (선택)</label>
					<textarea
						class="form-control"
						rows="3"
						maxlength="500"
						v-model="memo"
						placeholder="위온어스 담당자에게 전달할 내용을 입력하세요 (선택). 모든 담당자에게 동일하게 전달됩니다."
					></textarea>
				</div>

				<div class="mt-3" style="text-align: right">
					<button type="button" class="btn btn-outline-danger" @click="btnClear" :disabled="submitting">
						전체 비우기
					</button>
					<button
						type="button"
						class="btn btn-primary"
						@click="btnSubmit"
						:disabled="submitting || !selectedCount"
					>
						선택 전송 ({{ selectedCount }}건 / 담당자 {{ selectedGroupCount }}명)
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'PartnerCart',
	data() {
		return {
			list: [],
			memo: '',
			submitting: false,
			institutions: [],
			checked: {}, // key(building_type_building_uid) → bool
		};
	},
	computed: {
		institutionLabel() {
			const code = this.$store.getters.getInstitution;
			const found = this.institutions.find(i => i.code === code);
			return found ? found.label : code;
		},
		// 담당자(building_mem_uid)별 그룹. 미지정(null)은 맨 뒤로.
		groups() {
			const map = new Map();
			for (const it of this.list) {
				const uid = it.building_mem_uid || null;
				const gk = uid === null ? 'none' : String(uid);
				if (!map.has(gk)) {
					map.set(gk, {
						key: gk,
						mgrUid: uid,
						mgrName: uid ? it.building_mem_name || '#' + uid : null,
						mgrRank: it.building_mem_rank_name || '',
						items: [],
					});
				}
				map.get(gk).items.push(it);
			}
			return Array.from(map.values()).sort((a, b) => {
				if (a.mgrUid === null) return 1;
				if (b.mgrUid === null) return -1;
				return 0;
			});
		},
		selectedItems() {
			return this.list.filter(it => this.checked[it.building_type + '_' + it.building_uid]);
		},
		selectedCount() {
			return this.selectedItems.length;
		},
		// 선택 항목이 흩어진 담당자 그룹 수(= 생성될 전송 건수).
		selectedGroupCount() {
			const s = new Set();
			for (const it of this.selectedItems) s.add(it.building_mem_uid || 'none');
			return s.size;
		},
		allChecked: {
			get() {
				return this.list.length > 0 && this.list.every(it => this.checked[it.building_type + '_' + it.building_uid]);
			},
			set(v) {
				const c = {};
				for (const it of this.list) c[it.building_type + '_' + it.building_uid] = v;
				this.checked = c;
			},
		},
	},
	created() {
		this.loadInstitutions();
		this.getCart();
	},
	methods: {
		async loadInstitutions() {
			const re = await this.$apiGET('/partner/api/institutions');
			if (re && re.st && Array.isArray(re.list)) this.institutions = re.list;
		},
		async getCart() {
			const re = await this.$apiGET('/partner/api/cart');
			this.list = re && re.st && Array.isArray(re.list) ? re.list : [];
			// 기본 전체 선택.
			const c = {};
			for (const it of this.list) c[it.building_type + '_' + it.building_uid] = true;
			this.checked = c;
		},
		groupChecked(g) {
			return g.items.length > 0 && g.items.every(it => this.checked[it.building_type + '_' + it.building_uid]);
		},
		setGroupChecked(g, v) {
			for (const it of g.items) this.checked[it.building_type + '_' + it.building_uid] = v;
		},
		async btnRemove(it) {
			const re = await this.$apiPOST('/partner/api/cart/remove', {
				buildingType: it.building_type,
				bid: it.building_uid,
			});
			if (re && re.st) this.getCart();
		},
		async btnClear() {
			if (!window.confirm('담은 물건을 모두 비울까요?')) return;
			const re = await this.$apiPOST('/partner/api/cart/clear', {});
			if (re && re.st) this.getCart();
		},
		async btnSubmit() {
			const sel = this.selectedItems;
			if (!sel.length) {
				alert('전송할 물건을 선택하세요.');
				return;
			}
			const groupCount = this.selectedGroupCount;
			if (!window.confirm(`선택한 ${sel.length}건을 담당자별 ${groupCount}건으로 나눠 전송할까요?`)) return;
			this.submitting = true;
			const items = sel.map(it => ({ buildingType: it.building_type, bid: it.building_uid }));
			const re = await this.$apiPOST('/partner/api/submit', { items, memo: this.memo });
			this.submitting = false;
			if (re && re.st) {
				alert(`전송되었습니다. 담당자별 ${re.requestCount}건으로 나뉘어 위온어스 담당자가 확인합니다.`);
				this.memo = '';
				this.getCart();
			} else if (re && re.reason === 'empty_cart') {
				alert('선택한 물건이 카트에 없습니다.');
				this.getCart();
			}
		},
	},
};
</script>

<style scoped>
.cart-group {
	margin-bottom: 16px;
}
.cart-group-head {
	padding: 8px 10px;
	background: #f2f6ff;
	border-left: 3px solid #3b82f6;
	border-radius: 4px;
	margin-bottom: 4px;
}
</style>
